import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  ApiResponse,
  ArrayDataResponse,
  UserDownloadResponse,
  UserUIResponse,
} from "../../models/ApiResponse";
import agent from "../../api/agent";
import { UserResponse } from "../../models/responseModels/userResponse";
import {
  AddUserRequest,
  BulkUserRequest,
  UpdateUserRequest,
} from "../../models/requestModels/userRequest";
import { ResetPasswordRequest } from "../../models/requestModels/resetPasswordRequest";
import { AxiosResponse } from "axios";

type InitialState = {
  isLoading: boolean;
  userList: UserResponse[];
  errorMessage: string | null;
};

const initialState: InitialState = {
  isLoading: false,
  userList: [],
  errorMessage: null,
};

interface FetchFdpUserDocArgs {
  fileType: string;
  userId: string;
}

interface FetchFdpUserDocResponse {
  data: Blob;
  headers: any;
  status: number;
}

export const getUserList = createAsyncThunk<UserUIResponse>(
  "user/all",
  async () => {
    const response = await agent.UserAgent.getUserList();
    return response;
  }
);

export const updateUserStatus = createAsyncThunk<
  ApiResponse,
  { userIds: string[]; deleteStatus: boolean, activationPeriod?: number}
>("update/user/status", async ({ userIds, deleteStatus, activationPeriod}) => {
  const response = await agent.UserAgent.updateUserStatus(
    userIds,
    deleteStatus,
    activationPeriod
  );
  return response;
});

export const downloadUserDataToCsv = createAsyncThunk<
  UserDownloadResponse,
  UserResponse[]
>("user/downloadCsv", async (userData: UserResponse[]) => {
  const response = await agent.UserAgent.userCsvDownload(userData);
  return response;
});

export const downloadUserDataToXlsx = createAsyncThunk<Blob, UserResponse[]>(
  "user/downloadXlsx",
  async (userData: UserResponse[]) => {
    const response = await agent.UserAgent.userXlsxDownload(userData);
    return response;
  }
);

export const addUser = createAsyncThunk<
  ApiResponse,
  { formData: FormData; fdpId: string }
>("/user/new", async ({ formData, fdpId }) => {
  const response = await agent.UserAgent.addUser(formData, fdpId);
  return response;
});

export const updateUser = createAsyncThunk<ApiResponse, FormData>(
  "user/update",
  async (formData: FormData) => {
    const response = await agent.UserAgent.updateUser(formData);
    return response;
  }
);

export const addBulkUsers = createAsyncThunk<
  ArrayDataResponse,
  { bulkUserRequest: BulkUserRequest; fdpId: number }
>("/user/bulk/new", async ({ bulkUserRequest, fdpId }) => {
  const response = await agent.UserAgent.addBulkUser(bulkUserRequest, fdpId);
  return response;
});

export const deleteUser = createAsyncThunk<ApiResponse, UpdateUserRequest>(
  "user/deleteUser",
  async (updateUserRequest: UpdateUserRequest) => {
    const response = await agent.UserAgent.deleteUser(updateUserRequest);
    return response;
  }
);

export const resetUserPassword = createAsyncThunk<
  ApiResponse,
  ResetPasswordRequest
>(
  "auth/user/reset-password",
  async (resetPasswordRequest: ResetPasswordRequest) => {
    const response = await agent.UserAgent.resetUserPassword(
      resetPasswordRequest
    );
    return response;
  }
);

export const generateUserPassword = createAsyncThunk<ApiResponse, string>(
  "auth/user/generate-password",
  async (email: string) => {
    const response = await agent.UserAgent.generateUserPassword(email);
    return response;
  }
);

export const resetPasswordByToken = createAsyncThunk<
  ApiResponse,
  { resetPasswordRequest: ResetPasswordRequest; token: string }
>("auth/public/reset-password", async ({ resetPasswordRequest, token }) => {
  const response = await agent.UserAgent.resetPasswordByToken(
    resetPasswordRequest,
    token
  );
  return response;
});

export const forgotUserPassword = createAsyncThunk<ApiResponse, string>(
  "auth/public/user/forgot-password",
  async (username: string) => {
    const response = await agent.UserAgent.forgotUserPassword(username);
    return response;
  }
);

export const fetchFdpUserDoc = createAsyncThunk<
  FetchFdpUserDocResponse,
  FetchFdpUserDocArgs
>("auth/fdp/user/doc", async ({ fileType, userId }) => {
  const response: AxiosResponse<Blob> = await agent.UserAgent.fetchFdpUserDoc(
    fileType,
    userId
  );
  const { data, headers, status } = response;
  return { data, headers, status };
});

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getUserList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserList.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.isSuccess) {
          state.userList = action.payload.data;
        } else {
          state.userList = [];
        }
      })
      .addCase(getUserList.rejected, (state, action) => {
        state.isLoading = false;
        state.userList = [];
        state.errorMessage = action.error.message!;
      })
      .addCase(updateUserStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateUserStatus.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateUserStatus.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(addUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addUser.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addUser.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export default userSlice.reducer;
